/** Response shapes returned by the API (kept loose where the UI only displays values). */

export interface TradeView {
  id: string;
  symbol: string;
  instrumentId: string;
  market: { id: string; code: string };
  direction: 'LONG' | 'SHORT';
  status: 'OPEN' | 'CLOSED' | 'CANCELLED';
  isUnplanned: boolean;
  owner: { id: string; name: string };
  responsible: { id: string; name: string };
  account: { id: string | null; code: string; currency: string | null };
  openedAt: string;
  closedAt: string | null;
  avgEntry: string;
  stopLoss: string | null;
  takeProfits: { price: string; sizePct?: number }[];
  realisedR: string | null;
  money: boolean;
  sizeOpen: string | null;
  sizeInitial: string | null;
  initialRiskAmount: string | null;
  realisedPnl: string | null;
  currency: string | null;
  nextReport?: { dueAt: string; status: string; sequence: number | null } | null;
  /** INT-SYN-04: marked to the latest delayed price (null when there is none); money only when `money` (D8) */
  mark?: TradeMark | null;
}

export interface TradeMark {
  price: string;
  source: string;
  sourceName: string;
  sourceSymbol: string;
  proxyNote: string | null;
  asOf: string;
  fetchedAt: string | null;
  ageMinutes: number;
  stale: boolean;
  r: string | null;
  pct: string | null;
  money: boolean;
  pnl: string | null;
  currency: string | null;
}

export interface RiskCheckView {
  code: string;
  name: string;
  result: 'PASS' | 'ESCALATE' | 'REJECT' | 'NOT_APPLICABLE';
  value?: string;
  limit?: string;
  message: string;
}

export interface Evaluation {
  outcome: 'PASS' | 'ESCALATE' | 'REJECT';
  riskAmount: string | null;
  riskPct: string | null;
  rewardRisk: string[];
  fxRate: string | null;
  checks: RiskCheckView[];
}

export interface PlanView {
  id: string;
  status: string;
  trader: { id: string; name: string };
  account: { id: string; code: string; currency: string };
  instrument: { id: string; symbol: string; market: string; quoteCurrency: string };
  direction: 'LONG' | 'SHORT';
  orderType: string;
  plannedEntry: string;
  stopLoss: string | null;
  takeProfits: { price: string; sizePct?: number }[];
  size: string;
  riskAmount: string | null;
  riskPct: string | null;
  rewardRisk: string | null;
  rationale: string | null;
  submittedAt: string | null;
  approvalExpiresAt: string | null;
  riskCheck: { outcome: string; runAt: string; checks: RiskCheckView[]; fxRate: string | null } | null;
  approvals: { id: string; level: number; role: string; status: string; requestedAt: string; slaDueAt: string; decidedAt: string | null; decidedBy: { name: string } | null; reason: string | null }[];
  trade: { id: string; status: string } | null;
}

export interface AccountSnapshot {
  id: string;
  code: string;
  currency: string;
  marketId: string;
  equity: string;
  openRiskAmount: string;
  openTradeCount: number;
  realisedPnlToday: string;
  dailyLossUsedPct: string;
  dailyLossLocked: boolean;
  policy: { maxOpenRiskPct: string; dailyLossLimitPct: string; autoMaxRiskPct: string; hardMaxRiskPct: string };
}

export interface Instrument {
  id: string;
  marketId: string;
  symbol: string;
  displayName: string;
  quoteCurrency: string;
  tickSize: string;
  pointValue: string;
  sizeUnit: string;
  minSize: string;
  sizeStep: string;
  tvTicker?: string | null;
  priceSymbol?: string | null;
}

export const OUTCOME_TONE = { PASS: 'up', ESCALATE: 'warn', REJECT: 'down', NOT_APPLICABLE: 'neutral' } as const;
export const RESULT_COLOR: Record<string, string> = { PASS: 'text-up', ESCALATE: 'text-warn', REJECT: 'text-down', NOT_APPLICABLE: 'text-muted' };
